import { apiClient } from '@/lib/api/apiClient';
import { endpoints } from '@/lib/api/apiEndpoints';

export interface CategoryDto {
  id: string;
  name: string;
  nameAr?: string;
  description?: string;
  descriptionAr?: string;
  imageUrl?: string;
  sectionId?: string;
  sectionName?: string;
  parentCategoryId?: string;
  isActive: boolean;
  enableForNewUsers?: boolean;
  displayOrder: number;
  questionCount: number;
  subCategoryCount: number;
}

export interface CreateCategoryRequest {
  name: string;
  nameAr?: string;
  description?: string;
  descriptionAr?: string;
  imageUrl?: string;
  sectionId?: string;
  parentCategoryId?: string;
  isActive?: boolean;
  enableForNewUsers?: boolean;
  displayOrder?: number;
}

export interface UpdateCategoryRequest extends CreateCategoryRequest {
  categoryId: string;
}

export interface PagedResult<T> {
  items: T[];
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  totalCount: number;
}

export const categoryService = {
  async getAllCategories(params?: {
    includeInactive?: boolean;
    enableForNewUsers?: boolean | null;
    parentId?: string;
    pageNumber?: number;
    pageSize?: number;
  }): Promise<PagedResult<CategoryDto>> {
    const response = await apiClient.get(endpoints.admin.categories(), { params });
      const body = response?.data;
      const raw = body?.data ?? body?.Data ?? (typeof body === 'object' && body !== null ? body : {});
      const items = raw?.items ?? raw?.Items ?? [];
      return {
        items: Array.isArray(items) ? items : [],
        pageNumber: raw?.pageNumber ?? raw?.PageNumber ?? 1,
        pageSize: raw?.pageSize ?? raw?.PageSize ?? 20,
        totalPages: raw?.totalPages ?? raw?.TotalPages ?? 0,
        totalCount: raw?.totalCount ?? raw?.TotalCount ?? 0,
      };
  },

  async bulkUpdateEnableForNewUsers(categoryIds: string[], enable: boolean): Promise<{ updatedCount: number }> {
    const response = await apiClient.post(`${endpoints.admin.categories()}/bulk-enable-for-new-users`, {
      categoryIds,
      enable,
    });
    return response.data.data;
  },

  async createCategory(data: CreateCategoryRequest): Promise<CategoryDto> {
    const response = await apiClient.post(endpoints.admin.categories(), data);
    return response.data.data;
  },

  async updateCategory(id: string, data: UpdateCategoryRequest): Promise<CategoryDto> {
    const response = await apiClient.put(endpoints.admin.category(id), data);
    return response.data.data;
  },

  async toggleCategoryStatus(id: string): Promise<boolean> {
    const response = await apiClient.patch(`${endpoints.admin.category(id)}/toggle-status`);
    return response.data.data;
  },

  async deleteCategory(id: string): Promise<void> {
    await apiClient.delete(endpoints.admin.category(id));
  },
};
